import { UserIdentification } from "@sp-crm/core";
import posthog from "posthog-js";

let isConfigured = false;
let pendingCallbacks: Array<() => void> = [
    () => {
        posthog.reset();
    },
];

export const configureUserEvents = async () => {
    posthog.init("phc_d7GRBC0geaW6GXnp3Yzincyb8gEf6ibDeC5hESoeWBF", {
        api_host: "https://app.posthog.com",
        autocapture: false,
        capture_pageleave: false,
        capture_pageview: true,
        loaded: () => {
            isConfigured = true;
            pendingCallbacks.forEach(callback => callback());
            pendingCallbacks = [];
            startDarkModeTracking();
        },
    });
};

const run = (cb: () => void) => {
    if (isConfigured) {
        cb();
    } else {
        pendingCallbacks.push(cb);
    }
};

interface HasBeacon {
    Beacon?: (action: string, config?: unknown) => void;
}

let beaconInitialized = false;
let pendingBeaconIdentification: { email: string; name: string } | null = null;

export const onBeaconInitialized = (initialized: boolean) => {
    beaconInitialized = initialized;
    if (initialized && pendingBeaconIdentification) {
        const Beacon = (window as HasBeacon).Beacon;
        if (Beacon) {
            Beacon("identify", pendingBeaconIdentification);
        }
        pendingBeaconIdentification = null;
    }
};

export const identifyUser = (userIdentification: UserIdentification) => {
    const Beacon = (window as HasBeacon).Beacon;
    if (Beacon) {
        if (beaconInitialized) {
            Beacon("identify", {
                email: userIdentification.email,
                name: userIdentification.name,
            });
        } else {
            pendingBeaconIdentification = {
                email: userIdentification.email,
                name: userIdentification.name,
            };
        }
    }
    run(() => {
        posthog.identify(userIdentification.userId, {
            tenantId: userIdentification.tenantId,
            billableEntityId: userIdentification.billableEntityId,
        });
    });
};

export const logoutEvent = () => {
    run(() => {
        posthog.reset();
    });
    const Beacon = (window as HasBeacon).Beacon;
    if (Beacon && beaconInitialized) {
        Beacon("logout");
    }
    pendingBeaconIdentification = null;
};

const userEvent = [
    "2fa-disable-fail",
    "2fa-disable-start",
    "2fa-disable-success",
    "2fa-enable-fail",
    "2fa-enable-resent",
    "2fa-enable-start",
    "2fa-enable-success",
    "2fa-password-reset-start",
    "2fa-password-reset-fail",
    "2fa-password-reset-success",
    "2fa-password-reset-resend",
    "2fa-fail",
    "2fa-resend",
    "2fa-start",
    "2fa-success",
    "ai-summary-generate",
    "client-action",
    "client-status-change",
    "community-missing-recipients",
    "community-search-advanced-filter",
    "create-client",
    "initiate-voice-call",
    "message-send",
    "open-feature-announcements",
    "premium-callout",
    "request-community-availability",
    "request-community-availability-success",
    "request-community-availability-update",
    "request-community-availability-update-success",
    "sms-send",
    "sms-setup",
    "sms-thread-view",
    "task-complete",
    "task-create",
    "upload-client-form",
    "upload-client-form-start",
    "upload-client-form-upload",
    "user-dark-mode-preference",
    "user-layout-rendered",
    "user-login",
    "user-preview-design-toggle",
    "view-adhoc-report-results",
    "view-saved-report-results",
    "print-client-form",
    "print-community-form",
    "community-contact-signup-loaded",
    "community-contact-signup-submit",
    "community-contact-data-request-loaded",
    "community-contact-data-request-submit",
    "request-community-pricing",
    "request-community-pricing-success",
    "request-community-pricing-signup",
    "request-community-pricing-signup-success",
] as const;
type UserEvent = (typeof userEvent)[number];

export const handleEvent = (
    name: UserEvent,
    properties: Record<string, unknown> = {},
) => {
    run(() => {
        const eventProperties = {
            ...properties,
        };
        posthog.capture(name, eventProperties);
    });
};

const trackDarkModePreference = () => {
    if (typeof window === "undefined" || !window.matchMedia) {
        handleEvent("user-dark-mode-preference", {
            prefersDarkMode: false,
            colorScheme: "unknown",
        });
        return;
    }

    const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const isDarkMode = darkModeQuery.matches;

    handleEvent("user-dark-mode-preference", {
        prefersDarkMode: isDarkMode,
        colorScheme: isDarkMode ? "dark" : "light",
    });
};

const startDarkModeTracking = () => {
    trackDarkModePreference();

    if (typeof window === "undefined" || !window.matchMedia) {
        return;
    }

    const darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event: MediaQueryListEvent) => {
        handleEvent("user-dark-mode-preference", {
            prefersDarkMode: event.matches,
            colorScheme: event.matches ? "dark" : "light",
        });
    };

    darkModeQuery.addEventListener("change", handleChange);
};
